import React, { useEffect, useState } from 'react';
import { MobileLayout } from '../components/MobileLayout';
import { ArrowLeft, CheckCircle, Circle, Camera, Calendar, Clock, User, Sparkles, AlertTriangle, FileText, CheckSquare, ChevronRight, ChevronLeft, ZoomIn, X, Pencil, XCircle, Loader, CalendarClock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { CircularScore } from '../components/LastCleaningWidget';
import { Tabs } from '../components/ui/Tabs';
import { useScreenInit } from '../useScreenInit';
// Status of the cleaning being viewed
type CleaningStatus = 'pianificata' | 'in_corso' | 'completata';
// Mock Data
const photosData = [{
  id: 'soggiorno',
  name: 'Soggiorno',
  before: 'https://cdn.magicpatterns.com/uploads/3j17u41pZZqqZ7uBe8PFm8/_livingroom_messy.png',
  after: 'https://cdn.magicpatterns.com/uploads/dVhw2cGTmUhE4Ap2uCd1sm/_livingroom_clean.png'
}, {
  id: 'cucina',
  name: 'Cucina',
  before: 'https://cdn.magicpatterns.com/uploads/jUD5ZBDVxzRXjmRX6focQF/_kitchen_messy.png',
  after: 'https://cdn.magicpatterns.com/uploads/q7HEF8xL4ZfPHBQejbrvyX/_kitchen_clean.png'
}, {
  id: 'bagno',
  name: 'Bagno',
  before: 'https://cdn.magicpatterns.com/uploads/cK1fhi1rXfQ9v7iDq72qZ1/_bathroom_messy.png',
  after: 'https://cdn.magicpatterns.com/uploads/9cNyWn1us7ZSVXzYMKkr7V/_bathroom_clean.png'
}, {
  id: 'camera',
  name: 'Camera',
  before: 'https://cdn.magicpatterns.com/uploads/s9wFmfCssxKKstfcmFWPxC/_bedroom_messy.png',
  after: 'https://cdn.magicpatterns.com/uploads/vL5vGhBPD2XELKb3m3514D/_bedroom_clean.png'
}];
interface ChecklistItem {
  task: string;
  done: boolean;
  isDesiderata: boolean;
}
const checklistData: Record<string, ChecklistItem[]> = {
  Soggiorno: [{
    task: 'Pavimento aspirato e lavato',
    done: true,
    isDesiderata: false
  }, {
    task: 'Superfici spolverate',
    done: true,
    isDesiderata: false
  }, {
    task: 'Cuscini divano sistemati in modo specifico',
    done: true,
    isDesiderata: true
  }],
  Cucina: [{
    task: 'Piano cottura pulito',
    done: true,
    isDesiderata: false
  }, {
    task: 'Elettrodomestici lucidati',
    done: true,
    isDesiderata: false
  }, {
    task: 'Pavimento lavato',
    done: true,
    isDesiderata: false
  }],
  Bagno: [{
    task: 'Sanitari igienizzati',
    done: true,
    isDesiderata: false
  }, {
    task: 'Specchio pulito',
    done: true,
    isDesiderata: false
  }, {
    task: 'Asciugamani piegati a cigno',
    done: false,
    isDesiderata: true
  }],
  Camera: [{
    task: 'Letto rifatto con cura',
    done: true,
    isDesiderata: false
  }, {
    task: 'Pavimento aspirato e lavato',
    done: true,
    isDesiderata: false
  }, {
    task: 'Lenzuola sempre stirate',
    done: true,
    isDesiderata: true
  }],
  Generale: [{
    task: 'Spazzatura svuotata',
    done: true,
    isDesiderata: false
  }, {
    task: 'Finestre pulite',
    done: true,
    isDesiderata: false
  }, {
    task: 'Lenzuola sempre stirate',
    done: false,
    isDesiderata: true
  }]
};
const roomNotes: Record<string, string> = {
  Soggiorno: 'Divano macchia lato sinistro già presente prima della pulizia.',
  Cucina: '',
  Bagno: 'Silicone doccia da rifare, segnalato al proprietario.',
  Camera: ''
};
const roomSegnalazioni: Record<string, {
  title: string;
  severity: string;
  description: string;
}[]> = {
  Soggiorno: [],
  Cucina: [],
  Bagno: [{
    title: 'Silicone doccia deteriorato',
    severity: 'media',
    description: 'Il silicone nella zona doccia è ingiallito e si sta staccando. Consigliata sostituzione.'
  }],
  Camera: []
};
export function PuliziaDettaglioPage() {
  const [activeTab, setActiveTab] = useState('overview');
  // Resolve the cleaning status: Screen state → ?status= query → default completata
  const screenState = useScreenInit() as {
    status?: CleaningStatus;
  };
  const statusParam = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('status') : null;
  const status: CleaningStatus = screenState.status || (statusParam === 'pianificata' || statusParam === 'in_corso' || statusParam === 'completata' ? statusParam : 'completata');
  const isPlanned = status === 'pianificata';
  const isInProgress = status === 'in_corso';
  const isCompleted = status === 'completata';
  // Per-status presentation config
  const statusConfig = {
    pianificata: {
      label: 'Pianificata',
      subtitle: 'Pulizia programmata',
      badgeClass: 'bg-muted text-muted-foreground border-border',
      icon: <CalendarClock size={12} data-id="element-3258" />
    },
    in_corso: {
      label: 'In corso',
      subtitle: 'Pulizia in corso in questo momento',
      badgeClass: 'bg-coral/10 text-coral border-coral/20',
      icon: <span className="w-1.5 h-1.5 rounded-full bg-coral animate-pulse" data-id="element-3259" />
    },
    completata: {
      label: 'Completata',
      subtitle: 'Pulizia eseguita',
      badgeClass: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      icon: <CheckCircle size={12} data-id="element-3260" />
    }
  }[status];
  // Zoomed photo for the lightbox (null = closed). Stores which room and
  // which variant (before = disordinata / after = pulita) is shown so the
  // user can toggle between the two with arrows or the keyboard.
  const [zoomedPhoto, setZoomedPhoto] = useState<{
    roomId: string;
    variant: 'before' | 'after';
  } | null>(null);
  const zoomedRoom = zoomedPhoto ? photosData.find(r => r.id === zoomedPhoto.roomId) ?? null : null;
  const zoomedSrc = zoomedRoom ? zoomedPhoto?.variant === 'before' ? zoomedRoom.before : zoomedRoom.after : '';
  const zoomedAlt = zoomedRoom ? `${zoomedPhoto?.variant === 'before' ? 'Prima' : 'Dopo'} ${zoomedRoom.name}` : '';
  // Toggle between the before / after photo of the same room
  const toggleZoomVariant = () => setZoomedPhoto(prev => prev ? {
    ...prev,
    variant: prev.variant === 'before' ? 'after' : 'before'
  } : prev);
  // Close on Escape, switch photo with the left/right arrow keys
  useEffect(() => {
    if (!zoomedPhoto) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setZoomedPhoto(null);
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        toggleZoomVariant();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoomedPhoto]);
  // Calculate stats
  const allTasks = Object.values(checklistData).flat();
  const completedTasks = allTasks.filter(t => t.done).length;
  const totalTasks = allTasks.length;
  // Live progress for in-progress cleanings (partial completion)
  const inProgressDone = Math.round(totalTasks * 0.45);
  const renderChecklistItem = (item: ChecklistItem, idx: number) => {
    // Planned cleanings have nothing done yet; completed/in-progress use item.done
    const done = isPlanned ? false : item.done;
    return <li key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100" data-id="element-3261">
        <div className="mt-0.5 flex-shrink-0" data-id="element-3262">
          {done ? <CheckCircle size={18} className="text-emerald-500" data-id="element-3263" /> : <Circle size={18} className="text-muted-foreground" data-id="element-3264" />}
        </div>
        <div className="flex-1" data-id="element-3265">
          <span className={`text-sm ${done ? 'text-foreground' : 'text-muted-foreground'}`} data-id="element-3266">
            {item.task}
          </span>
          {item.isDesiderata && <div className="flex items-center gap-1 mt-1.5" data-id="element-3267">
              <Sparkles size={12} className="text-muted-foreground" data-id="element-3268" />
              <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider" data-id="element-3269">
                Desiderata Host
              </span>
            </div>}
        </div>
      </li>;
  };
  return <MobileLayout data-id="element-3270">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6" data-id="element-3271">
        <Link to="/dashboard" className="p-2 hover:bg-muted rounded-full transition-colors flex-shrink-0" data-id="element-3272">
          <ArrowLeft size={20} className="text-foreground" data-id="element-3273" />
        </Link>
        <div className="min-w-0" data-id="element-3274">
          <div className="flex items-center gap-2 flex-wrap" data-id="element-3275">
            <h1 className="text-xl lg:text-2xl font-bold text-foreground" data-id="element-3276">
              Dettaglio Pulizia
            </h1>
            <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border ${statusConfig.badgeClass}`} data-id="element-3277">
              {statusConfig.icon}
              {statusConfig.label}
            </span>
          </div>
        </div>
      </div>

      {/* Top Info Bar - Property card header */}
      <div className="bg-white border border-border rounded-xl mb-6 shadow-sm w-full" data-id="element-3278">
        {/* Top row: Cleaner identity + AI score */}
        <div className="flex items-center justify-between gap-4 p-5" data-id="element-3279">
          <div className="flex items-center gap-3 min-w-0" data-id="element-3280">
            <img src="https://cdn.magicpatterns.com/uploads/g4qUZyyXcdzW3wf29pvDcz/Screenshot_2026-03-22_alle_16.12.18.png" alt="Francesca N." className="w-11 h-11 rounded-full object-cover flex-shrink-0" data-id="element-3281" />
            <div className="min-w-0" data-id="element-3282">
              <div className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium" data-id="element-3283">
                Cleaner
              </div>
              <div className="text-sm font-semibold text-foreground truncate" data-id="element-3284">
                Francesca N.
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-shrink-0" data-id="element-3285">
            {isCompleted && <CircularScore score={98} data-id="element-3286" />}
            {isInProgress && <div className="flex flex-col items-end" data-id="element-3287">
                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-coral bg-coral/10 border border-coral/20 px-2.5 py-1 rounded-full" data-id="element-3288">
                  <span className="w-1.5 h-1.5 rounded-full bg-coral animate-pulse" data-id="element-3289" />
                  In corso
                </span>
                <span className="text-[11px] text-muted-foreground mt-1" data-id="element-3290">
                  {inProgressDone}/{totalTasks} task
                </span>
              </div>}
            {isPlanned && <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground bg-muted border border-border px-2.5 py-1 rounded-full" data-id="element-3291">
                <CalendarClock size={14} data-id="element-3292" />
                Pianificata
              </span>}
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-border" data-id="element-3293" />

        {/* Bottom row: meta line */}
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap sm:items-center gap-x-5 gap-y-3 p-5" data-id="element-3294">
          {/* Data */}
          <div className="flex items-center gap-2 min-w-0" data-id="element-3295">
            <Calendar size={15} className="text-coral flex-shrink-0" data-id="element-3296" />
            <span className="text-sm font-medium text-foreground truncate" data-id="element-3297">
              Oggi, 15 Gen
            </span>
          </div>

          {/* Separator (desktop) */}
          <span className="hidden sm:inline text-border" data-id="element-3298">·</span>

          {/* Orario */}
          <div className="flex items-center gap-2 min-w-0" data-id="element-3299">
            <Clock size={15} className="text-coral flex-shrink-0" data-id="element-3300" />
            <span className="text-sm font-medium text-foreground truncate" data-id="element-3301">
              10:30–13:00 · 2h 30m
            </span>
          </div>

          {/* Separator (desktop) */}
          <span className="hidden sm:inline text-border" data-id="element-3302">·</span>

          {/* Guest */}
          <div className="flex items-center gap-2 min-w-0 col-span-2 sm:col-auto" data-id="element-3303">
            <User size={15} className="text-coral flex-shrink-0" data-id="element-3304" />
            <span className="text-sm font-medium text-foreground truncate" data-id="element-3305">
              Marco R.
            </span>
            <span className="text-xs text-muted-foreground flex-shrink-0" data-id="element-3306">
              · Booking.com
            </span>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <Tabs className="mb-2 -mx-4 px-4 lg:mx-0 lg:px-0" scrollable value={activeTab} onValueChange={setActiveTab} items={[{
      id: 'overview',
      label: 'Overview'
    }, ...photosData.map(room => ({
      id: room.id,
      label: room.name
    }))]} data-id="element-3307" />

      {/* Content Area - Full Width */}
      <div className="w-full space-y-6" data-id="element-3308">
        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && <>
            {/* Summary Stats */}
            <div className="bg-white border border-border rounded-xl p-5 shadow-sm" data-id="element-3309">
              <h2 className="text-base font-bold text-foreground mb-4" data-id="element-3310">
                Riepilogo Pulizia
              </h2>
              <div className="grid grid-cols-3 gap-4 divide-x divide-border" data-id="element-3311">
                {isCompleted && <>
                    <div className="text-center px-2" data-id="element-3312">
                      <div className="text-2xl font-bold text-foreground" data-id="element-3313">
                        {completedTasks}/{totalTasks}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1" data-id="element-3314">
                        Task completati
                      </div>
                    </div>
                    <div className="text-center px-2" data-id="element-3315">
                      <div className="text-2xl font-bold text-foreground" data-id="element-3316">
                        2h 30m
                      </div>
                      <div className="text-xs text-muted-foreground mt-1" data-id="element-3317">
                        Durata effettiva
                      </div>
                    </div>
                    <div className="text-center px-2" data-id="element-3318">
                      <div className="text-2xl font-bold text-foreground" data-id="element-3319">
                        98
                        <span className="text-sm text-muted-foreground font-normal" data-id="element-3320">
                          /100
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1" data-id="element-3321">
                        Punteggio qualità
                      </div>
                    </div>
                  </>}
                {isInProgress && <>
                    <div className="text-center px-2" data-id="element-3322">
                      <div className="text-2xl font-bold text-foreground" data-id="element-3323">
                        {inProgressDone}/{totalTasks}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1" data-id="element-3324">
                        Task completati
                      </div>
                    </div>
                    <div className="text-center px-2" data-id="element-3325">
                      <div className="text-2xl font-bold text-foreground" data-id="element-3326">
                        1h 10m
                      </div>
                      <div className="text-xs text-muted-foreground mt-1" data-id="element-3327">
                        Tempo trascorso
                      </div>
                    </div>
                    <div className="text-center px-2" data-id="element-3328">
                      <div className="inline-flex items-center gap-1.5 text-base font-bold text-coral" data-id="element-3329">
                        <Loader size={18} className="animate-spin" data-id="element-3330" />
                        Live
                      </div>
                      <div className="text-xs text-muted-foreground mt-1" data-id="element-3331">
                        Stato
                      </div>
                    </div>
                  </>}
                {isPlanned && <>
                    <div className="text-center px-2" data-id="element-3332">
                      <div className="text-2xl font-bold text-foreground" data-id="element-3333">
                        {totalTasks}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1" data-id="element-3334">
                        Task previsti
                      </div>
                    </div>
                    <div className="text-center px-2" data-id="element-3335">
                      <div className="text-2xl font-bold text-foreground" data-id="element-3336">
                        ~2h 30m
                      </div>
                      <div className="text-xs text-muted-foreground mt-1" data-id="element-3337">
                        Durata stimata
                      </div>
                    </div>
                    <div className="text-center px-2" data-id="element-3338">
                      <div className="text-2xl font-bold text-foreground" data-id="element-3339">
                        10:30
                      </div>
                      <div className="text-xs text-muted-foreground mt-1" data-id="element-3340">
                        Inizio previsto
                      </div>
                    </div>
                  </>}
              </div>

              {/* Live progress bar for in-progress cleanings */}
              {isInProgress && <div className="mt-4" data-id="element-3341">
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5" data-id="element-3342">
                    <span data-id="element-3343">Avanzamento</span>
                    <span className="font-medium text-coral" data-id="element-3344">
                      {Math.round(inProgressDone / totalTasks * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2 overflow-hidden" data-id="element-3345">
                    <div className="bg-coral h-2 rounded-full transition-all duration-500" style={{
                width: `${inProgressDone / totalTasks * 100}%`
              }} data-id="element-3346" />
                  </div>
                </div>}
            </div>

            {/* Status action bar */}
            {isPlanned && <div className="bg-white border border-border rounded-xl p-4 shadow-sm flex flex-col sm:flex-row gap-3" data-id="element-3347">
                <button className="flex-1 inline-flex items-center justify-center gap-2 px-4 h-11 rounded-xl text-sm font-medium bg-coral text-white hover:bg-coral-600 transition-colors" data-id="element-3348">
                  <Pencil size={16} data-id="element-3349" />
                  Modifica pianificazione
                </button>
                <button className="flex-1 inline-flex items-center justify-center gap-2 px-4 h-11 rounded-xl text-sm font-medium border border-red-200 text-red-600 hover:bg-red-50 transition-colors" data-id="element-3350">
                  <XCircle size={16} data-id="element-3351" />
                  Annulla pulizia
                </button>
              </div>}
            {isInProgress && <div className="bg-coral/5 border border-coral/20 rounded-xl p-4 flex items-center gap-3" data-id="element-3352">
                <Loader size={18} className="text-coral animate-spin flex-shrink-0" data-id="element-3353" />
                <p className="text-sm text-foreground" data-id="element-3354">
                  La pulizia è in corso. Il punteggio qualità CleanAI e le foto
                  saranno disponibili al termine.
                </p>
              </div>}

            {/* General Checklist */}
            <div className="bg-white border border-border rounded-xl p-5 shadow-sm" data-id="element-3355">
              <h2 className="text-base font-bold text-foreground flex items-center gap-2 mb-4" data-id="element-3356">
                <CheckSquare size={18} className="text-muted-foreground" data-id="element-3357" />
                Checklist Generale
              </h2>
              <ul className="space-y-3" data-id="element-3358">
                {checklistData.Generale.map((item, idx) => renderChecklistItem(item, idx))}
              </ul>
            </div>

            {/* Room Summaries */}
            <div className="space-y-4" data-id="element-3359">
              <h2 className="text-base font-bold text-foreground" data-id="element-3360">
                Dettaglio Stanze
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-3361">
                {photosData.map(room => {
              const roomTasks = checklistData[room.name] || [];
              const roomCompleted = roomTasks.filter(t => t.done).length;
              const roomTotal = roomTasks.length;
              const hasIssues = (roomSegnalazioni[room.name]?.length ?? 0) > 0;
              const hasNotes = !!roomNotes[room.name];
              return <button key={room.id} onClick={() => setActiveTab(room.id)} className="bg-white border border-border rounded-xl p-4 shadow-sm hover:border-coral/50 hover:shadow transition-all text-left flex flex-col h-full" data-id="element-3362">
                      <div className="aspect-video rounded-lg overflow-hidden border border-border mb-3 bg-muted" data-id="element-3363">
                        <img src={room.after} alt={`Dopo ${room.name}`} className="w-full h-full object-cover" data-id="element-3364" />
                      </div>
                      <div className="flex items-center justify-between mb-2" data-id="element-3365">
                        <h3 className="font-semibold text-foreground" data-id="element-3366">
                          {room.name}
                        </h3>
                        <ChevronRight size={16} className="text-muted-foreground" data-id="element-3367" />
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3" data-id="element-3368">
                        <CheckCircle size={14} className={roomCompleted === roomTotal ? 'text-emerald-500' : 'text-muted-foreground'} data-id="element-3369" />
                        <span data-id="element-3370">
                          {roomCompleted}/{roomTotal} completate
                        </span>
                      </div>
                      <div className="mt-auto flex gap-2" data-id="element-3371">
                        {hasIssues && <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200" data-id="element-3372">
                            <AlertTriangle size={10} className="mr-1" data-id="element-3373" />
                            Segnalazioni
                          </span>}
                        {hasNotes && !hasIssues && <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-coral/5 text-coral border border-coral/30" data-id="element-3374">
                            <FileText size={10} className="mr-1" data-id="element-3375" />
                            Note
                          </span>}
                      </div>
                    </button>;
            })}
              </div>
            </div>
          </>}

        {/* ROOM TABS */}
        {photosData.map(room => {
        if (activeTab !== room.id) return null;
        const roomTasks = checklistData[room.name] || [];
        const notes = roomNotes[room.name];
        const issues = roomSegnalazioni[room.name] || [];
        return <div key={room.id} className="space-y-6" data-id="element-3376">
              {/* Planned: no photos yet */}
              {isPlanned && <div className="bg-white border border-border rounded-xl p-5 shadow-sm flex items-start gap-3" data-id="element-3377">
                  <CalendarClock size={18} className="text-muted-foreground flex-shrink-0 mt-0.5" data-id="element-3378" />
                  <p className="text-sm text-muted-foreground" data-id="element-3379">
                    Le foto Prima / Dopo saranno disponibili una volta svolta la
                    pulizia.
                  </p>
                </div>}
              {/* In progress: photos coming soon */}
              {isInProgress && <div className="bg-white border border-border rounded-xl p-5 shadow-sm flex items-start gap-3" data-id="element-3380">
                  <Camera size={18} className="text-coral flex-shrink-0 mt-0.5" data-id="element-3381" />
                  <p className="text-sm text-muted-foreground" data-id="element-3382">
                    Pulizia in corso: le foto verranno caricate man mano dal
                    cleaner.
                  </p>
                </div>}
              {/* Photos Section (completed only) */}
              {isCompleted && <div className="bg-white border border-border rounded-xl p-5 shadow-sm" data-id="element-3383">
                  <h2 className="text-base font-bold text-foreground flex items-center gap-2 mb-4" data-id="element-3384">
                    <Camera size={18} className="text-muted-foreground" data-id="element-3385" />
                    Foto Prima / Dopo
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3386">
                    <div className="space-y-2" data-id="element-3387">
                      <div className="inline-block px-3 py-1 bg-amber-50 text-amber-700 text-xs font-bold rounded-md border border-amber-100" data-id="element-3388">
                        Prima
                      </div>
                      <button type="button" onClick={() => setZoomedPhoto({
                  roomId: room.id,
                  variant: 'before'
                })} aria-label={`Ingrandisci foto prima ${room.name}`} className="group relative block w-full aspect-[4/3] rounded-lg overflow-hidden border border-border bg-muted cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-coral" data-id="element-3389">
                        <img src={room.before} alt={`Prima ${room.name}`} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" data-id="element-3390" />
                        <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors" data-id="element-3391">
                          <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 text-foreground rounded-full p-2 shadow-sm" data-id="element-3392">
                            <ZoomIn size={18} data-id="element-3393" />
                          </span>
                        </span>
                      </button>
                    </div>
                    <div className="space-y-2" data-id="element-3394">
                      <div className="inline-block px-3 py-1 bg-emerald-50 text-emerald-700 text-xs font-bold rounded-md border border-emerald-100" data-id="element-3395">
                        Dopo
                      </div>
                      <button type="button" onClick={() => setZoomedPhoto({
                  roomId: room.id,
                  variant: 'after'
                })} aria-label={`Ingrandisci foto dopo ${room.name}`} className="group relative block w-full aspect-[4/3] rounded-lg overflow-hidden border border-border bg-muted cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-coral" data-id="element-3396">
                        <img src={room.after} alt={`Dopo ${room.name}`} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" data-id="element-3397" />
                        <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors" data-id="element-3398">
                          <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 text-foreground rounded-full p-2 shadow-sm" data-id="element-3399">
                            <ZoomIn size={18} data-id="element-3400" />
                          </span>
                        </span>
                      </button>
                    </div>
                  </div>
                </div>}

              {/* Checklist Section */}
              <div className="bg-white border border-border rounded-xl p-5 shadow-sm" data-id="element-3401">
                <h2 className="text-base font-bold text-foreground flex items-center gap-2 mb-4" data-id="element-3402">
                  <CheckSquare size={18} className="text-muted-foreground" data-id="element-3403" />
                  Checklist {room.name}
                  {isPlanned && <span className="text-xs font-normal text-muted-foreground" data-id="element-3404">
                      (da svolgere)
                    </span>}
                </h2>
                <ul className="space-y-3" data-id="element-3405">
                  {roomTasks.map((item, idx) => renderChecklistItem(item, idx))}
                </ul>
              </div>

              {/* Notes Section */}
              {!isPlanned && notes && <div className="bg-white border border-border rounded-xl p-5 shadow-sm" data-id="element-3406">
                  <h2 className="text-base font-bold text-foreground flex items-center gap-2 mb-3" data-id="element-3407">
                    <FileText size={18} className="text-muted-foreground" data-id="element-3408" />
                    Note del Cleaner
                  </h2>
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-100 text-sm text-foreground" data-id="element-3409">
                    {notes}
                  </div>
                </div>}

              {/* Segnalazioni Section */}
              {!isPlanned && issues.length > 0 && <div className="bg-white border border-border rounded-xl p-5 shadow-sm" data-id="element-3410">
                  <h2 className="text-base font-bold text-foreground flex items-center gap-2 mb-4" data-id="element-3411">
                    <AlertTriangle size={18} className="text-muted-foreground" data-id="element-3412" />
                    Segnalazioni
                  </h2>
                  <div className="space-y-3" data-id="element-3413">
                    {issues.map((issue, idx) => <div key={idx} className="p-4 bg-amber-50/50 rounded-lg border border-amber-100" data-id="element-3414">
                        <div className="flex items-start justify-between mb-2" data-id="element-3415">
                          <h3 className="font-semibold text-foreground" data-id="element-3416">
                            {issue.title}
                          </h3>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200" data-id="element-3417">
                            {issue.severity}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground" data-id="element-3418">
                          {issue.description}
                        </p>
                      </div>)}
                  </div>
                </div>}
            </div>;
      })}
      </div>

      {/* Photo Lightbox */}
      {zoomedPhoto && zoomedRoom && <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 cursor-zoom-out" onClick={() => setZoomedPhoto(null)} role="dialog" aria-modal="true" aria-label={zoomedAlt} data-id="element-3419">
          <button type="button" onClick={() => setZoomedPhoto(null)} aria-label="Chiudi" className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors" data-id="element-3420">
            <X size={24} data-id="element-3421" />
          </button>

          <button type="button" onClick={e => {
        e.stopPropagation();
        toggleZoomVariant();
      }} aria-label="Foto precedente" className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 p-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors" data-id="element-3422">
            <ChevronLeft size={26} data-id="element-3423" />
          </button>

          <button type="button" onClick={e => {
        e.stopPropagation();
        toggleZoomVariant();
      }} aria-label="Foto successiva" className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 p-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors" data-id="element-3424">
            <ChevronRight size={26} data-id="element-3425" />
          </button>

          <div className="flex flex-col items-center gap-3" onClick={e => e.stopPropagation()} data-id="element-3426">
            <img src={zoomedSrc} alt={zoomedAlt} className="max-w-full max-h-[82vh] w-auto h-auto object-contain rounded-lg shadow-2xl cursor-default" data-id="element-3427" />
            <div className="inline-flex items-center gap-1 p-1 bg-white/10 rounded-full" data-id="element-3428">
              <button type="button" onClick={() => setZoomedPhoto(prev => prev ? {
            ...prev,
            variant: 'before'
          } : prev)} aria-pressed={zoomedPhoto.variant === 'before'} className={`px-4 h-8 rounded-full text-xs font-semibold transition-colors ${zoomedPhoto.variant === 'before' ? 'bg-white text-foreground' : 'text-white/80 hover:text-white'}`} data-id="element-3429">
                Prima
              </button>
              <button type="button" onClick={() => setZoomedPhoto(prev => prev ? {
            ...prev,
            variant: 'after'
          } : prev)} aria-pressed={zoomedPhoto.variant === 'after'} className={`px-4 h-8 rounded-full text-xs font-semibold transition-colors ${zoomedPhoto.variant === 'after' ? 'bg-white text-foreground' : 'text-white/80 hover:text-white'}`} data-id="element-3430">
                Dopo
              </button>
            </div>
            <p className="text-xs text-white/60" data-id="element-3431">
              Usa ← → per passare da disordinata a pulita
            </p>
          </div>
        </div>}
    </MobileLayout>;
}